// PLAN.md §2.3 + §7.4 — DiceBear bottts avatars with per-seed LRU memoization.

import { useMemo } from 'react'
import { createAvatar } from '@dicebear/core'
import { bottts } from '@dicebear/collection'
import { DeltaNumber } from './ported/index.js'

const LRU_CAP = 1000
const cache = new Map<string, string>()

function dataUriFor(seed: string): string {
  const cached = cache.get(seed)
  if (cached !== undefined) {
    // Bump recency: re-insert.
    cache.delete(seed)
    cache.set(seed, cached)
    return cached
  }
  const svg = createAvatar(bottts, { seed, size: 56 }).toDataUri()
  cache.set(seed, svg)
  if (cache.size > LRU_CAP) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) cache.delete(oldest)
  }
  return svg
}

export type AvatarProps = {
  seed: string
  isMe?: boolean
  /** Cumulative PnL since the game's first $5 entry (PLAN.md §7.5). */
  cumulativePnl?: number | null
  /** PLAN.md §2.3 — death-watch outline pulse (red, animated). */
  deathPulse?: boolean
  /** PLAN.md §3.4 — render as tombstone (B&W + hatching). */
  tombstone?: boolean
  /** PLAN.md §3.3 optimistic move — render at 60% opacity. */
  ghost?: boolean
  /** Scale factor — PLAN.md §2.3 crowded-cell formula. */
  scale?: number
}

export function Avatar({
  seed,
  isMe = false,
  cumulativePnl,
  deathPulse = false,
  tombstone = false,
  ghost = false,
  scale = 1,
}: AvatarProps) {
  const uri = useMemo(() => dataUriFor(seed), [seed])

  const wrapperCls = [
    'relative inline-flex flex-col items-center',
    deathPulse ? 'outline outline-1.5 outline-red rounded-full animate-death-pulse' : '',
    isMe ? 'outline outline-2 outline-black rounded-full -outline-offset-1' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const imgCls = [
    'w-7 h-7 rounded-full',
    tombstone ? 'grayscale opacity-40' : '',
    ghost ? 'opacity-60' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span
      className={wrapperCls}
      style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'bottom center' } : undefined}
    >
      {!tombstone && cumulativePnl != null && Math.abs(cumulativePnl) >= 0.01 && (
        <span className="absolute -top-4 pointer-events-none">
          <DeltaNumber value={cumulativePnl} format="usd" size="sm" animate precision={2} />
        </span>
      )}
      {!tombstone && cumulativePnl != null && Math.abs(cumulativePnl) < 0.01 && (
        <span className="absolute -top-4 text-label tabular-nums text-grey">$0.00</span>
      )}
      <img src={uri} className={imgCls} alt="" draggable={false} />
    </span>
  )
}
